import { buildProps, definePropType } from '@ll-plus/utils'

import type { ExtractPropTypes, VNode } from 'vue'
import type { AvatarProps } from '../../avatar/src/avatar'
import type RankingList from './ranking-list.vue'

/**
 * 排行榜单条数据
 */
export interface RankingListItem {
  /** 名称 */
  label?: string
  /** 统计数值 */
  value?: number
  /** 描述（副标题） */
  description?: string
  /** 头像，传入 ll-avatar 的 props 对象 */
  avatar?: Partial<AvatarProps>
  [key: string]: any
}

/**
 * 字段映射：当数据字段名与默认 label/value/description/avatar 不一致时使用
 */
export interface RankingListFieldNames {
  label?: string
  value?: string
  description?: string
  avatar?: string
}

/**
 * 经过排序、截断、计算占比后的渲染数据
 */
export interface RankingListRenderItem {
  /** 排名（从 1 开始） */
  rank: number
  /** 名称 */
  label: string
  /** 原始数值 */
  value: number
  /** 格式化后的数值文本 */
  valueText: string
  /** 描述 */
  description: string
  /** 头像 props */
  avatar?: Partial<AvatarProps>
  /** 相对最大值的占比（0 ~ 100） */
  percent: number
  /** 原始数据 */
  raw: RankingListItem
}

/** 排序规则：desc 正序（从大到小，默认），asc 倒序（从小到大） */
export type RankingListOrder = 'desc' | 'asc'

/** 展示样式：podium 奖台（默认），list 列表 */
export type RankingListType = 'podium' | 'list'

export interface RankingListSlots {
  /** 自定义整行内容 */
  item?: (props: RankingListRenderItem) => VNode[]
  /** 自定义头像 */
  avatar?: (props: RankingListRenderItem) => VNode[]
  /** 自定义名称 */
  label?: (props: RankingListRenderItem) => VNode[]
  /** 自定义数值 */
  value?: (props: RankingListRenderItem) => VNode[]
  /** 空数据 */
  empty?: () => VNode[]
}

/** 排行榜最大可展示条数 */
export const RANKING_LIST_MAX_LIMIT = 500

export const rankingListProps = buildProps({
  /**
   * @description 排行榜数据，格式为 { label, value, description }
   */
  data: {
    type: definePropType<RankingListItem[]>(Array),
    default: () => [],
  },
  /**
   * @description 字段映射，将数据中的字段映射为 label / value / description
   */
  fieldNames: {
    type: definePropType<RankingListFieldNames>(Object),
    default: () => ({}),
  },
  /**
   * @description 排序规则，desc 正序（从大到小，默认）、asc 倒序（从小到大）
   */
  order: {
    type: definePropType<RankingListOrder>(String),
    values: ['desc', 'asc'],
    default: 'desc',
  },
  /**
   * @description 展示数量，最多 500 条
   */
  limit: {
    type: Number,
    default: 10,
  },
  /**
   * @description 自定义背景色
   */
  background: {
    type: String,
    default: '',
  },
  /**
   * @description 字体颜色
   */
  color: {
    type: String,
    default: '',
  },
  /**
   * @description 进度条颜色
   */
  barColor: {
    type: String,
    default: '',
  },
  /**
   * @description 数字格式化函数，返回展示的数值文本
   */
  valueFormatter: {
    type: definePropType<(value: number, item: RankingListItem) => string>(
      Function,
    ),
    default: undefined,
  },
  /**
   * @description 数值是否使用千分位分隔符（未设置 valueFormatter 时生效）
   */
  thousandSeparator: {
    type: Boolean,
    default: true,
  },
  /**
   * @description 是否显示进度条
   */
  showBar: {
    type: Boolean,
    default: false,
  },
  /**
   * @description 展示样式，podium 奖台（默认）、list 列表
   */
  type: {
    type: definePropType<RankingListType>(String),
    values: ['podium', 'list'],
    default: 'podium',
  },
  /**
   * @description 是否显示头像
   */
  showAvatar: {
    type: Boolean,
    default: true,
  },
} as const)


export const RankingListEmits = {
  'item-click': (e: MouseEvent, item: RankingListRenderItem) => !!item
}

export type RankingListProps = ExtractPropTypes<typeof rankingListProps>

export type RankingListInstance = InstanceType<typeof RankingList>
